import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Single source of truth for every gated dashboard action.
 * Combines: roles + agent_permissions + internal_staff_permissions
 *           + whether the user's agency also owns a developer_profile.
 *
 * Use these flags everywhere instead of inline `hasRole(...)` checks
 * so the sidebar, dashboard panels, and route guards stay in sync.
 */
export interface DashboardPermissions {
  // Common
  canViewSavedProperties: boolean;
  canViewMessages: boolean;
  canViewNotifications: boolean;

  // Listings (properties / plots / commercial)
  canViewMyListings: boolean;
  canAddProperty: boolean;
  canAddPlot: boolean;
  canAddCommercial: boolean;
  canViewAgencyListings: boolean;

  // Projects (developer-owned)
  canViewMyProjects: boolean;
  canAddProject: boolean;
  canManageBookings: boolean; // bookings, plot map, transfers, inventory

  // Leads
  canViewLeads: boolean;
  canViewLeadPipeline: boolean;
  canManageClients: boolean;
  canAssignLeads: boolean;

  // Analytics & reviews
  canViewAnalytics: boolean;
  canViewReviews: boolean;

  // Agency / team
  canManageAgencyMembers: boolean;
  canManageAgentPermissions: boolean;

  // Internal staff
  canViewInternalTasks: boolean;
  canCreateInternalTasks: boolean;
  canAssignInternalTasks: boolean;
  canManageInternalTeam: boolean;
  canManageProspects: boolean;
  canManageCampaigns: boolean;
  canViewDepartmentAnalytics: boolean;

  // Quality control & Ad operations
  canAuditListings: boolean;
  canManageFeatured: boolean;
  canManageAdCampaigns: boolean;

  // Approvals (delegated to internal teams)
  canApproveListings: boolean;     // Superadmin + Quality Control + team-scoped
  canApproveApplications: boolean; // Superadmin + Sales Support team + team-scoped
  canModerateReviews: boolean;     // Superadmin + QC + team-scoped
  canHandleReports: boolean;       // Superadmin + Customer Support + team-scoped
  // Superadmin only
  canManageUsers: boolean;
  canManageDepartments: boolean;

  // Insights (Phase 4)
  canViewInsights: boolean;

  // Reasons (for tooltips on disabled buttons — granular off but role allows)
  reasons: {
    addProperty?: string;
    addPlot?: string;
    addCommercial?: string;
    addProject?: string;
    viewAnalytics?: string;
    manageClients?: string;
    viewLeads?: string;
  };
}

const EMPTY: DashboardPermissions = {
  canViewSavedProperties: false,
  canViewMessages: false,
  canViewNotifications: false,
  canViewMyListings: false,
  canAddProperty: false,
  canAddPlot: false,
  canAddCommercial: false,
  canViewAgencyListings: false,
  canViewMyProjects: false,
  canAddProject: false,
  canManageBookings: false,
  canViewLeads: false,
  canViewLeadPipeline: false,
  canManageClients: false,
  canAssignLeads: false,
  canViewAnalytics: false,
  canViewReviews: false,
  canManageAgencyMembers: false,
  canManageAgentPermissions: false,
  canViewInternalTasks: false,
  canCreateInternalTasks: false,
  canAssignInternalTasks: false,
  canManageInternalTeam: false,
  canManageProspects: false,
  canManageCampaigns: false,
  canViewDepartmentAnalytics: false,
  canAuditListings: false,
  canManageFeatured: false,
  canManageAdCampaigns: false,
  canApproveListings: false,
  canApproveApplications: false,
  canModerateReviews: false,
  canHandleReports: false,
  canManageUsers: false,
  canManageDepartments: false,
  canViewInsights: false,
  reasons: {},
};

export function useDashboardPermissions(): { permissions: DashboardPermissions; isLoading: boolean } {
  const { user, hasRole } = useAuth();

  // Fetch agent permissions (only relevant when user is an agent)
  const { data: agentPerm } = useQuery({
    queryKey: ['dash-perms-agent', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('agent_permissions')
        .select('can_add_listings, can_edit_listings, can_view_all_leads, can_manage_clients, can_view_analytics')
        .eq('agent_user_id', user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user && hasRole('agent'),
  });

  // (Removed: previously gated Add Project on whether user's agency was also a developer.
  //  Project uploads are now strictly limited to users with the `developer` role.)

  // Internal staff permissions
  const { data: staffPerm } = useQuery({
    queryKey: ['dash-perms-staff', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('internal_staff_permissions')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user && (
      hasRole('sales_agent') || hasRole('sales_team_leader') || hasRole('sales_manager') ||
      hasRole('marketing_agent') || hasRole('marketing_team_leader') || hasRole('marketing_manager') ||
      hasRole('it_staff') || hasRole('accounting_staff') || hasRole('hr_staff') ||
      hasRole('quality_control_lead') || hasRole('quality_control_agent') ||
      hasRole('ad_operations_lead') || hasRole('ad_operations_agent')
    ),
  });

  // Team-scope: aggregate scope arrays from teams the user belongs to
  const { data: teamScope = [] } = useQuery({
    queryKey: ['dash-perms-team-scope', user?.id],
    queryFn: async () => {
      const { data: memberships } = await supabase
        .from('internal_team_members')
        .select('team_id')
        .eq('user_id', user!.id);
      const teamIds = (memberships || []).map((m: any) => m.team_id);
      if (!teamIds.length) return [];
      const { data: teams } = await supabase
        .from('internal_teams')
        .select('scope')
        .in('id', teamIds);
      const all = (teams || []).flatMap((t: any) => t.scope || []);
      return [...new Set(all)];
    },
    enabled: !!user,
  });

  if (!user) return { permissions: EMPTY, isLoading: false };

  const isSuperadmin = hasRole('superadmin');
  const isAgencyAdmin = hasRole('agency_admin');
  const isAgent = hasRole('agent');
  const isDeveloper = hasRole('developer');
  const isSalesLeader = hasRole('sales_team_leader');
  const isSalesManager = hasRole('sales_manager');
  const isSalesAgent = hasRole('sales_agent');
  const isMarketingLeader = hasRole('marketing_team_leader');
  const isMarketingManager = hasRole('marketing_manager');
  const isMarketingAgent = hasRole('marketing_agent');
  const isQC = hasRole('quality_control_lead') || hasRole('quality_control_agent');
  const isAdOps = hasRole('ad_operations_lead') || hasRole('ad_operations_agent');
  const isInternalStaff = isSalesAgent || isSalesLeader || isSalesManager
    || isMarketingAgent || isMarketingLeader || isMarketingManager
    || hasRole('it_staff') || hasRole('accounting_staff') || hasRole('hr_staff')
    || isQC || isAdOps;

  // Agent default: can add listings unless explicitly disabled
  const agentCanAdd = agentPerm ? agentPerm.can_add_listings : true;
  const agentCanViewAnalytics = agentPerm ? agentPerm.can_view_analytics : false;
  const agentCanManageClients = agentPerm ? agentPerm.can_manage_clients : true;
  const agentCanViewAllLeads = agentPerm ? agentPerm.can_view_all_leads : false;

  // Operator listings — agency_admin / agent only. Superadmin governs the platform; they don't list.
  const canAddListing = isAgencyAdmin || (isAgent && agentCanAdd);
  // Project uploads: developers only.
  const canAddProject = isDeveloper;

  const reasons: DashboardPermissions['reasons'] = {};
  if (isAgent && !agentCanAdd) {
    reasons.addProperty = 'Listing permission disabled by your agency';
    reasons.addPlot = 'Listing permission disabled by your agency';
    reasons.addCommercial = 'Listing permission disabled by your agency';
  }
  if ((isAgent || isAgencyAdmin) && !isDeveloper && !isSuperadmin) {
    reasons.addProject = 'Only verified developers can add projects';
  }
  if (isAgent && !agentCanViewAnalytics) {
    reasons.viewAnalytics = 'Analytics access disabled by your agency';
  }
  if (isAgent && !agentCanManageClients) {
    reasons.manageClients = 'Client management disabled by your agency';
  }

  const permissions: DashboardPermissions = {
    // Everyone signed-in can do these (except superadmin doesn't need a buyer "saved" tab)
    canViewSavedProperties: !isSuperadmin,
    canViewMessages: true,
    canViewNotifications: true,

    // Listings — operator-only (superadmin governs via approvals/moderation)
    canViewMyListings: isAgencyAdmin || isAgent,
    canAddProperty: canAddListing,
    canAddPlot: canAddListing,
    canAddCommercial: canAddListing,
    canViewAgencyListings: isAgencyAdmin || isAgent,

    // Projects — developer-only
    canViewMyProjects: isDeveloper,
    canAddProject,
    canManageBookings: isDeveloper,

    // Leads — operator-only (developer for project leads; superadmin uses Insights instead)
    canViewLeads: isAgencyAdmin || isAgent || isDeveloper,
    canViewLeadPipeline: isAgencyAdmin || isAgent,
    canManageClients: isAgencyAdmin || (isAgent && agentCanManageClients),
    canAssignLeads: isAgencyAdmin || isSalesLeader || isSalesManager,

    // Analytics — superadmin keeps platform-wide analytics
    canViewAnalytics:
      isSuperadmin || isAgencyAdmin || isDeveloper ||
      (isAgent && agentCanViewAnalytics) ||
      (isInternalStaff && (staffPerm?.can_view_department_analytics || isSalesManager || isMarketingManager)),
    // Personal "Reviews" tab is an agency feature; superadmin uses Reviews Moderation instead
    canViewReviews: isAgencyAdmin || isAgent,

    // Agency / team — agency owns its team
    canManageAgencyMembers: isAgencyAdmin,
    canManageAgentPermissions: isAgencyAdmin,

    // Internal staff
    canViewInternalTasks: isSuperadmin || isInternalStaff,
    canCreateInternalTasks: isSuperadmin || isSalesLeader || isSalesManager
      || isMarketingLeader || isMarketingManager
      || (!!staffPerm?.can_create_tasks && isInternalStaff),
    canAssignInternalTasks: isSuperadmin || isSalesLeader || isSalesManager
      || isMarketingLeader || isMarketingManager
      || !!staffPerm?.can_assign_tasks,
    canManageInternalTeam: isSuperadmin || isSalesManager || isMarketingManager
      || !!staffPerm?.can_manage_team_members,
    canManageProspects: isSuperadmin || isSalesAgent || isSalesLeader || isSalesManager
      || !!staffPerm?.can_manage_prospects,
    canManageCampaigns: isSuperadmin || isMarketingLeader || isMarketingManager
      || !!staffPerm?.can_manage_campaigns,
    canViewDepartmentAnalytics: isSuperadmin || isSalesManager || isMarketingManager
      || !!staffPerm?.can_view_department_analytics,

    // Quality control & Ad ops
    canAuditListings: isSuperadmin || isQC,
    canManageFeatured: isSuperadmin || isAdOps,
    canManageAdCampaigns: isSuperadmin || isAdOps,

    // Approvals (delegated): role OR team-scope grants
    canApproveListings: isSuperadmin || isQC
      || teamScope.includes('approve_listings') || teamScope.includes('approve_projects') || teamScope.includes('approve_plots'),
    canApproveApplications: isSuperadmin || isSalesAgent || isSalesLeader || isSalesManager
      || teamScope.includes('approve_agents') || teamScope.includes('approve_agencies') || teamScope.includes('approve_developers'),
    canModerateReviews: isSuperadmin || isQC || teamScope.includes('moderate_reviews'),
    canHandleReports: isSuperadmin || teamScope.includes('handle_reports'),
    // Superadmin only
    canManageUsers: isSuperadmin,
    canManageDepartments: isSuperadmin,

    // Insights — superadmin + sales managers + sales team leaders + ad ops leads
    canViewInsights: isSuperadmin || isSalesManager || isSalesLeader || hasRole('ad_operations_lead'),

    reasons,
  };

  return { permissions, isLoading: false };
}
